import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../styles/VideoPlayer.css';

const VideoPlayer = ({ 
  src, 
  poster, 
  title,
  onError,
  autoPlay = false,
  controls = true,
  showTitle = true,
  allowFullScreen = true,
  className = ''
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [error, setError] = useState(null);
  const [currentSrc, setCurrentSrc] = useState('');
  const [attemptCount, setAttemptCount] = useState(0);
  
  // Format time in MM:SS format
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Handle video errors
  const handleError = (e) => {
    // Set appropriate error message based on error code
    const videoElement = e.target;
    let errorMessage = 'An error occurred while loading the video.';
    let debugInfo = '';
    
    if (videoElement.error) {
      switch (videoElement.error.code) {
        case 1: // MEDIA_ERR_ABORTED
          errorMessage = 'Video playback was aborted.';
          break;
        case 2: // MEDIA_ERR_NETWORK
          errorMessage = 'A network error caused the video download to fail. This might be due to CORS issues.';
          debugInfo = `Attempted to load: ${currentSrc}`;
          break;
        case 3: // MEDIA_ERR_DECODE
          errorMessage = 'The video could not be decoded. The file might be corrupted or in an unsupported format.';
          break;
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
          errorMessage = 'The video format is not supported by your browser or there might be a CORS issue.';
          debugInfo = `Attempted to load: ${currentSrc}`;
          break;
        default:
          errorMessage = `An unknown error occurred. Code: ${videoElement.error.code}`;
      }
    }
    
    // If we've already tried multiple sources, add more detailed error info
    if (attemptCount > 0) {
      debugInfo += `\nAttempt #${attemptCount + 1} failed.`;
    }
    
    setError({ message: errorMessage, debug: debugInfo });
    
    // Call the onError callback if provided
    if (onError) {
      onError(errorMessage, e);
    }
    
    // If this is the first error and we haven't tried alternate URLs yet, try automatically
    if (attemptCount === 0) {
      retryWithAlternateUrl();
    }
  };
  
  // Retry loading the video with a different URL format
  const retryWithAlternateUrl = () => {
    setError(null);
    setAttemptCount(prev => prev + 1);
    
    // Try to construct an alternate URL
    let alternateUrl = '';
    
    // Extract the filename from the current URL
    let filename = '';
    if (currentSrc.includes('/')) {
      const parts = currentSrc.split('/');
      filename = parts[parts.length - 1];
    }
    
    // Check if we're in local development
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalDev) {
      // Try different URL patterns based on the attempt count
      switch (attemptCount % 7) {
        case 0:
          // Try direct video endpoint with explicit port
          alternateUrl = `${window.location.protocol}//${window.location.hostname}:3000/video/${filename}`;
          break;
        case 1:
          // Try uploads path with explicit port
          alternateUrl = `${window.location.protocol}//${window.location.hostname}:3000/uploads/videos/${filename}`;
          break;
        case 2:
          // Try API endpoint with explicit port
          alternateUrl = `${window.location.protocol}//${window.location.hostname}:3000/api/lessons/video/${filename}`;
          break;
        case 3:
          // Try with proxy path
          alternateUrl = `/api/video/${filename}`;
          break;
        case 4:
          // Try direct path without port
          alternateUrl = `/video/${filename}`;
          break;
        case 5:
          // Try direct uploads path
          alternateUrl = `/uploads/videos/${filename}`;
          break;
        case 6:
          // Try with query parameter to bypass cache
          alternateUrl = `${currentSrc}?t=${new Date().getTime()}`;
          break;
      }
    } else {
      // For production, use the original patterns
      switch (attemptCount % 4) {
        case 0:
          // Try direct video endpoint
          alternateUrl = `${window.location.origin}/video/${filename}`;
          break;
        case 1:
          // Try uploads path
          alternateUrl = `${window.location.origin}/uploads/videos/${filename}`;
          break;
        case 2:
          // Try API endpoint
          alternateUrl = `${window.location.origin}/api/lessons/video/${filename}`;
          break;
        case 3:
          // Try with query parameter to bypass cache
          alternateUrl = `${currentSrc}?t=${new Date().getTime()}`;
          break;
      }
    }
    
    // Update the video element
    if (videoRef.current) {
      videoRef.current.src = alternateUrl;
      setCurrentSrc(alternateUrl);
      videoRef.current.load();
    }
  };
  
  // Update time display
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };
  
  // Set duration when metadata is loaded
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      // Reset error state on successful load
      setError(null);
    }
  };
  
  // Handle play/pause state
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  // Handle fullscreen toggle
  const toggleFullScreen = () => {
    if (!videoRef.current) return;
    
    if (!isFullScreen) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };
  
  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.msFullscreenElement
      );
    };
    
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);
  
  // Prepare the video source URL
  const getVideoUrl = () => {
    if (!src) return '';
    
    // Check if we're in local development
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // If it's already a full URL, use it as is
    if (src.startsWith('http://') || src.startsWith('https://')) {
      return src;
    }
    
    // For local development, handle URLs differently
    if (isLocalDev) {
      // If it starts with /video/, try to access the backend directly
      if (src.startsWith('/video/')) {
        const filename = src.split('/video/')[1];
        const fullUrl = `${window.location.protocol}//${window.location.hostname}:3000/video/${filename}`;
        return fullUrl;
      }
      
      // If it starts with /uploads/, try to access the backend directly
      if (src.startsWith('/uploads/')) {
        const fullUrl = `${window.location.protocol}//${window.location.hostname}:3000${src}`;
        return fullUrl;
      }
    }
    
    // If it's a relative path starting with a slash, prepend the current origin
    if (src.startsWith('/')) {
      const fullUrl = `${window.location.origin}${src}`;
      return fullUrl;
    }
    
    // Otherwise, assume it's a relative path and prepend the current origin
    const fullUrl = `${window.location.origin}/${src}`;
    return fullUrl;
  };
  
  // Check if a URL is accessible
  const checkUrlAccessibility = async (url) => {
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        mode: 'cors',
        cache: 'no-cache'
      });
      
      return response.ok;
    } catch (error) {
      return false;
    }
  };
  
  // Check if a video file exists at a given URL
  const checkVideoExists = async (url) => {
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        mode: 'cors',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        // Check content type to make sure it's a video
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.startsWith('video/')) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  };
  
  // Initialize the video source
  useEffect(() => {
    const initializeVideo = async () => {
    const url = getVideoUrl();
    setCurrentSrc(url);
      
    // Reset attempt count when source changes
    setAttemptCount(0);
    setError(null);
      
      if (!url) return;
      
      // For local development, try to directly access the file if it's a video-TIMESTAMP-RANDOM.mp4 format
      const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocalDev) {
        // Check if the URL contains a filename that matches the pattern video-TIMESTAMP-RANDOM.mp4
        const filenameMatch = url.match(/video-\d+-\d+\.\w+$/);
        if (filenameMatch) {
          const filename = filenameMatch[0];
          
          // Try direct access to the file
          const directUrl = `${window.location.protocol}//${window.location.hostname}:3000/uploads/videos/${filename}`;
          
          const videoExists = await checkVideoExists(directUrl);
          if (videoExists) {
            videoRef.current.src = directUrl;
            setCurrentSrc(directUrl);
            videoRef.current.load();
            return;
          }
        }
      }
      
      // Check if the URL is accessible
      const isAccessible = await checkUrlAccessibility(url);
      if (!isAccessible) {
        retryWithAlternateUrl();
        return;
      }
      
      // Check if it's a valid video file
      const videoExists = await checkVideoExists(url);
      if (!videoExists) {
        retryWithAlternateUrl();
      }
    };
    
    initializeVideo();
  }, [src]);
  
  return (
    <div className={`video-player-container ${className}`}>
      {showTitle && title && (
        <div className="video-title">{title}</div>
      )}
      
      <div className="video-wrapper">
        {error ? (
          <div className="video-error">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="error-message">{error.message}</div>
            {error.debug && (
              <div className="error-debug small text-muted mt-1">
                {error.debug}
              </div>
            )}
            <div className="d-flex gap-2 mt-2">
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  setError(null);
                  if (videoRef.current) {
                    videoRef.current.load();
                  }
                }}
              >
                Try Again
              </button>
              <button 
                className="btn btn-sm btn-outline-secondary"
                onClick={retryWithAlternateUrl}
              >
                Try Alternate URL
              </button>
              {currentSrc && currentSrc.includes('/video/') && (
                <button 
                  className="btn btn-sm btn-outline-info"
                  onClick={() => {
                    // Extract filename from /video/ path and try direct uploads path
                    const filename = currentSrc.split('/video/')[1].split('?')[0];
                    const directUrl = `${window.location.protocol}//${window.location.hostname}:3000/uploads/videos/${filename}`;
                    console.log('Trying direct uploads path:', directUrl);
                    
                    setError(null);
                    if (videoRef.current) {
                      videoRef.current.src = directUrl;
                      setCurrentSrc(directUrl);
                      videoRef.current.load();
                    }
                  }}
                >
                  Try Direct Path
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
          <video
            ref={videoRef}
              className="video-element"
            src={currentSrc}
            poster={poster}
            controls={controls}
            autoPlay={autoPlay}
              crossOrigin="anonymous"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
              onError={handleError}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          >
            Your browser does not support the video tag.
          </video>
            
            {/* Custom controls can be added here if needed */}
          </>
        )}
      </div>
    </div>
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  title: PropTypes.string,
  onError: PropTypes.func,
  autoPlay: PropTypes.bool,
  controls: PropTypes.bool,
  showTitle: PropTypes.bool,
  allowFullScreen: PropTypes.bool,
  className: PropTypes.string
};

export default VideoPlayer; 